import * as React from "react";
import { useNavigate } from "react-router";

import Button from "@mui/material/Button";
import DialogContent from "@mui/material/DialogContent";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";

import { canShareRecap } from "../../util/nativeShare";
import { SESSION_RECAP_FEATURE } from "../SessionRecap/feature";
import CloseableDialogTitle from "../common/CloseableDialogTitle";
import SupportLink from "../common/SupportLink";
import { useCurrentUser } from "../hooks/currentUser";
import { usePairingContext } from "../hooks/pairingStatus";

import useDialog from "./useDialog";

interface ShutdownChecklistDialogProps {
    shutdown: boolean;
}

function ChecklistDialog({
    shutdown,
}: ShutdownChecklistDialogProps): React.JSX.Element {
    const { unpair, sessionId } = usePairingContext();
    const { dialogType, setDialogType } = useDialog();
    const { isAdmin, features } = useCurrentUser();
    const navigate = useNavigate();
    // Held from when the dialog opened. Every flow that opens this checklist
    // has just unpaired, and unpairing keeps the session id (only a
    // server-sent sessionClosed drops it), so this is the session that ended.
    const ended = React.useRef({
        sessionId,
        showRecap:
            features.includes(SESSION_RECAP_FEATURE) &&
            !isAdmin() &&
            canShareRecap(),
        navigate,
    });

    React.useEffect(() => {
        unpair();
    }, [unpair]);

    // Leaving the checklist, however it's closed (Finished, the X, the
    // backdrop, Escape), is the end of the session: show its recap, for
    // members with the feature, where it can be shared. Staff end sessions
    // all day testing trainers, so they're spared.
    React.useEffect(
        () => () => {
            const { sessionId: id, showRecap, navigate: go } = ended.current;
            if (id && showRecap) go(`/sessions/${id}/recap?source=end_flow`);
        },
        [],
    );

    const handleClose = React.useCallback(() => {
        setDialogType(null);
    }, [setDialogType]);

    return (
        <>
            <CloseableDialogTitle variant="h4" onClose={handleClose}>
                Please Clean-up the Court
            </CloseableDialogTitle>
            <DialogContent dividers>
                <List
                    sx={{
                        listStyleType:
                            dialogType === "ShutdownCleanupChecklist"
                                ? "decimal"
                                : "disc",
                        pl: 3,
                        "& .MuiListItem-root": {
                            display: "list-item",
                        },
                    }}
                >
                    <ListItem>Empty the ball bin if necessary.</ListItem>
                    <ListItem>
                        Store the trainer and balls in the designated area.
                    </ListItem>
                    {shutdown && (
                        <ListItem>
                            It may take up to a minute for the two lights in the
                            back to turn off. You can put the trainer away in
                            the meantime.
                        </ListItem>
                    )}
                </List>
                <Stack direction="column" spacing={1}>
                    <Button
                        variant="contained"
                        color="secondary"
                        size="large"
                        fullWidth
                        onClick={handleClose}
                    >
                        Finished
                    </Button>
                </Stack>
            </DialogContent>
            <SupportLink preText="Having Trouble?">Click here.</SupportLink>
        </>
    );
}

export function ShutdownChecklistDialog(): React.JSX.Element {
    return <ChecklistDialog shutdown />;
}

export function DisconnectCleanupChecklistDialog(): React.JSX.Element {
    return <ChecklistDialog shutdown={false} />;
}
